import { create } from 'zustand';
import { TaskInfo, AppNode, NodeStatus } from '../types';
import { config } from '../config';
import { generateApi, GenerateResult } from '../services/generateApi';
import { findStrategy, GenerationContext } from '../services/generationStrategies';
import { eventBus, CanvasEvents } from '../utils/eventBus';
import { useNodeStore } from './nodeStore';
import { unifiedStorage } from '../services/unifiedStorage';

interface TaskState {
  pendingTasks: Map<string, TaskInfo>;
  addTask: (taskId: string, nodeId: string) => void;
  addTaskWithRequest: (taskId: string, nodeId: string, nodeType: string, request: Record<string, any>) => void;
  updateTaskStatus: (taskId: string, status: TaskInfo['status']) => void;
  updateTasksBatch: (updates: Array<{ taskId: string; status: TaskInfo['status'] }>) => void;
  removeTask: (taskId: string) => void;
  getTaskByNodeId: (nodeId: string) => TaskInfo | undefined;
  loadFromLocalStorage: () => void;
  saveToLocalStorage: () => void;
  checkTaskStatus: (taskId: string) => Promise<TaskInfo['status']>;
  resumePendingTasks: (
    updateNode: (id: string, data: Partial<AppNode['data']>, size?: { width?: number; height?: number }, title?: string, status?: NodeStatus) => void,
    handleAssetGenerated: (type: 'image' | 'video' | 'audio', src: string, title: string) => void,
    currentProjectId: string | null,
    onTaskCompleted?: (task: TaskInfo, updates: Partial<AppNode['data']>) => void,
  ) => void;
  connectWebSocket: () => void;
  disconnectWebSocket: () => void;
}

const STORAGE_KEY = 'bong_ai_pending_tasks';
const TASKS_INDEXEDDB_KEY = 'pending_tasks';
const _pollingTaskIds = new Set<string>();

let wsConnection: WebSocket | null = null;
let wsReconnectTimer: ReturnType<typeof setTimeout> | null = null;
let wsReconnectAttempts = 0;
const WS_MAX_RECONNECT_ATTEMPTS = 10;
// 首次重连 5 秒（给 Render 冷启动时间），后续指数退避
const WS_BASE_RECONNECT_DELAY = 5000;
let wsManuallyClosed = false;

export const useTaskStore = create<TaskState>((set, get) => ({
  pendingTasks: new Map(),

  addTask: (taskId, nodeId) => {
    set((state) => {
      const newMap = new Map(state.pendingTasks);
      newMap.set(taskId, {
        taskId,
        nodeId,
        status: 'RUNNING',
        startTime: Date.now(),
      });
      return { pendingTasks: newMap };
    });
    get().saveToLocalStorage();
  },

  addTaskWithRequest: (taskId, nodeId, nodeType, request) => {
    set((state) => {
      const newMap = new Map(state.pendingTasks);
      newMap.set(taskId, {
        taskId,
        nodeId,
        status: 'RUNNING',
        startTime: Date.now(),
        nodeType,
        request,
      });
      return { pendingTasks: newMap };
    });
    get().saveToLocalStorage();
  },

  updateTaskStatus: (taskId, status) => {
    set((state) => {
      const newMap = new Map(state.pendingTasks);
      const task = newMap.get(taskId);
      if (task) {
        newMap.set(taskId, { ...task, status });
      }
      return { pendingTasks: newMap };
    });
    get().saveToLocalStorage();
  },

  updateTasksBatch: (updates) => {
    set((state) => {
      const newMap = new Map(state.pendingTasks);
      let changed = false;
      for (const { taskId, status } of updates) {
        const task = newMap.get(taskId);
        if (task && task.status !== status) {
          newMap.set(taskId, { ...task, status });
          changed = true;
        }
      }
      if (!changed) return state;
      return { pendingTasks: newMap };
    });
    get().saveToLocalStorage();
  },

  removeTask: (taskId) => {
    set((state) => {
      const newMap = new Map(state.pendingTasks);
      newMap.delete(taskId);
      return { pendingTasks: newMap };
    });
    get().saveToLocalStorage();
  },

  getTaskByNodeId: (nodeId) => {
    const state = get();
    for (const task of state.pendingTasks.values()) {
      if (task.nodeId === nodeId) {
        return task;
      }
    }
    return undefined;
  },

  loadFromLocalStorage: () => {
    // 异步从 IndexedDB 加载（兼容旧版 localStorage 数据）
    (async () => {
      try {
        // 优先从 IndexedDB 读取
        const tasks = await unifiedStorage.loadFromIndexedDB<TaskInfo[]>(TASKS_INDEXEDDB_KEY);
        if (tasks && tasks.length > 0) {
          const newMap = new Map<string, TaskInfo>();
          tasks.forEach((task) => {
            newMap.set(task.taskId, task);
          });
          set({ pendingTasks: newMap });
          return;
        }
        // 回退到 localStorage（兼容旧数据）
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const tasks = JSON.parse(stored) as TaskInfo[];
          const newMap = new Map<string, TaskInfo>();
          tasks.forEach((task) => {
            newMap.set(task.taskId, task);
          });
          set({ pendingTasks: newMap });
        }
      } catch (e) {
        console.error('Failed to load tasks from storage:', e);
      }
    })();
  },

  saveToLocalStorage: () => {
    try {
      const state = get();
      const tasks = Array.from(state.pendingTasks.values());
      // 异步写入 IndexedDB（不阻塞调用方，IndexedDB 容量大不会配额超限）
      unifiedStorage.saveToIndexedDB(TASKS_INDEXEDDB_KEY, tasks).catch((e) => {
        console.error('Failed to save tasks to IndexedDB:', e);
      });
    } catch (e) {
      console.error('Failed to save tasks to storage:', e);
    }
  },

  checkTaskStatus: async (taskId) => {
    try {
      const data = await generateApi.getTaskStatus(taskId);
      const statusMap: Record<string, TaskInfo['status']> = {
        pending: 'RUNNING',
        running: 'RUNNING',
        success: 'SUCCESS',
        failed: 'FAILED',
      };
      const status = statusMap[data.status] || 'FAILED';
      get().updateTaskStatus(taskId, status);
      return status;
    } catch (e) {
      console.error('Failed to check task status:', e);
      return 'FAILED';
    }
  },

  resumePendingTasks: (updateNode, handleAssetGenerated, currentProjectId, onTaskCompleted) => {
    const { pendingTasks } = get();
    const runningTasks: TaskInfo[] = [];
    pendingTasks.forEach((task) => {
      if (task.status === 'RUNNING') {
        runningTasks.push(task);
      }
    });

    if (runningTasks.length === 0) return;

    for (const task of runningTasks) {
      if (_pollingTaskIds.has(task.taskId)) continue;
      _pollingTaskIds.add(task.taskId);

      (async () => {
        try {
          console.log(`[TaskResume] Resuming task ${task.taskId} for node ${task.nodeId}`);

          const existingNode = useNodeStore?.getState?.()?.nodeMap?.get(task.nodeId);
          if (!existingNode) {
            console.warn(`[TaskResume] Node ${task.nodeId} no longer exists, removing task ${task.taskId}`);
            get().removeTask(task.taskId);
            _pollingTaskIds.delete(task.taskId);
            return;
          }

          updateNode(task.nodeId, { error: undefined, progress: '恢复任务中...', _needsTaskRecovery: false }, undefined, undefined, NodeStatus.WORKING);

          const statusResult = await generateApi.pollTaskUntilComplete(task.taskId, (status) => {
            if (status.status === 'running') {
              updateNode(task.nodeId, { progress: '生成中...' }, undefined, undefined, NodeStatus.WORKING);
            }
          });

          if (statusResult.status === 'failed') {
            throw new Error(statusResult.error || '任务失败');
          }

          const resultData = statusResult.result;
          if (!resultData) {
            throw new Error('未收到生成结果');
          }

          const nodeType = task.nodeType || '';
          const strategy = findStrategy(nodeType as any);
          const request = task.request || {};
          const prompt = request.prompt || '';

          if (strategy) {
            const mockNode: AppNode = {
              id: task.nodeId,
              type: nodeType as any,
              x: 0, y: 0,
              title: '',
              status: NodeStatus.WORKING,
              data: {
                model: request.modelId,
                prompt: request.prompt,
                aspectRatio: request.params?.aspectRatio,
                resolution: request.params?.resolution,
                imageCount: request.params?.imageCount,
                duration: request.params?.duration,
                videoCount: request.params?.videoCount,
                generationMode: request.params?.generationMode,
                generateAudio: request.params?.generateAudio,
                panoramaQuality: request.params?.quality,
              },
              inputs: [],
            };

            const rawResult: GenerateResult = {
              taskId: statusResult.taskId,
              status: statusResult.status,
              results: resultData.results,
              error: resultData.error,
              videoMetadata: resultData.videoMetadata,
              analysis: resultData.analysis,
            };

            const ctx: GenerationContext = {
              projectId: currentProjectId,
              onProgress: () => {},
              onAssetGenerated: handleAssetGenerated,
              onTaskCreated: () => {},
            };

            const updates = await strategy.processResult(mockNode, rawResult, ctx, prompt);
            updateNode(task.nodeId, updates, undefined, undefined, NodeStatus.SUCCESS);

            if (updates.image) handleAssetGenerated('image', updates.image, '');
            if (updates.videoUri) handleAssetGenerated('video', updates.videoUri, '');
            if (updates.audioUri) handleAssetGenerated('audio', updates.audioUri, '');

            if (onTaskCompleted) {
              onTaskCompleted(task, updates);
            }
          } else {
            updateNode(task.nodeId, {}, undefined, undefined, NodeStatus.SUCCESS);
          }

          get().updateTaskStatus(task.taskId, 'SUCCESS');
          get().removeTask(task.taskId);
          _pollingTaskIds.delete(task.taskId);
          console.log(`[TaskResume] Task ${task.taskId} resumed successfully`);
        } catch (e: any) {
          console.error(`[TaskResume] Failed to resume task ${task.taskId}:`, e);
          updateNode(task.nodeId, { error: e.message || '恢复任务失败', progress: undefined }, undefined, undefined, NodeStatus.ERROR);
          get().updateTaskStatus(task.taskId, 'FAILED');
          get().removeTask(task.taskId);
          _pollingTaskIds.delete(task.taskId);
        }
      })();
    }
  },

  connectWebSocket: () => {
    wsManuallyClosed = false;
    if (wsConnection && (wsConnection.readyState === WebSocket.OPEN || wsConnection.readyState === WebSocket.CONNECTING)) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = config.apiBase.replace(/^https?:/, protocol);
    const url = `${wsUrl}/ws/tasks?client_id=bong-ai-front`;

    try {
      wsConnection = new WebSocket(url);

      wsConnection.onopen = () => {
        console.log('[TaskWS] Connected');
        wsReconnectAttempts = 0;
      };

      wsConnection.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'task_update' && msg.task_id && msg.data) {
            const wsStatusMap: Record<string, TaskInfo['status']> = {
              pending: 'RUNNING',
              running: 'RUNNING',
              success: 'SUCCESS',
              failed: 'FAILED',
            };
            const status = wsStatusMap[msg.data.status] || (msg.data.status as TaskInfo['status']);
            if (status) {
              get().updateTaskStatus(msg.task_id, status);

              if (status === 'SUCCESS' || status === 'FAILED') {
                const task = get().pendingTasks.get(msg.task_id);
                if (task && task.nodeId && task.nodeId.startsWith('agent-node-')) {
                  eventBus.emit(CanvasEvents.AGENT_TASK_STATUS_CHANGED, {
                    taskId: msg.task_id,
                    nodeId: task.nodeId,
                    status,
                  });

                  if (status === 'SUCCESS' && !_pollingTaskIds.has(msg.task_id)) {
                    eventBus.emit(CanvasEvents.AGENT_TASK_COMPLETED, {
                      taskId: msg.task_id,
                      nodeId: task.nodeId,
                      status: 'SUCCESS',
                      updates: msg.data.result || {},
                    });
                  }
                } else if (task && task.nodeId && status === 'SUCCESS' && !_pollingTaskIds.has(msg.task_id)) {
                  eventBus.emit(CanvasEvents.TASK_COMPLETED, {
                    taskId: msg.task_id,
                    nodeId: task.nodeId,
                    nodeType: task.nodeType || '',
                    request: task.request || {},
                    result: msg.data.result || {},
                  });
                }
                get().removeTask(msg.task_id);
                _pollingTaskIds.delete(msg.task_id);
              }
            }
          }
        } catch {}
      };

      wsConnection.onclose = () => {
        console.log('[TaskWS] Disconnected');
        wsConnection = null;
        if (wsManuallyClosed) return;
        if (wsReconnectAttempts >= WS_MAX_RECONNECT_ATTEMPTS) {
          console.warn('[TaskWS] Max reconnect attempts reached, giving up');
          return;
        }
        const delay = WS_BASE_RECONNECT_DELAY * Math.pow(1.5, wsReconnectAttempts);
        wsReconnectAttempts++;
        console.log(`[TaskWS] Reconnecting in ${Math.round(delay)}ms (attempt ${wsReconnectAttempts}/${WS_MAX_RECONNECT_ATTEMPTS})...`);
        wsReconnectTimer = setTimeout(() => {
          get().connectWebSocket();
        }, delay);
      };

      wsConnection.onerror = () => {
        wsConnection?.close();
      };
    } catch {
      if (wsManuallyClosed) return;
      if (wsReconnectAttempts < WS_MAX_RECONNECT_ATTEMPTS) {
        const delay = WS_BASE_RECONNECT_DELAY * Math.pow(1.5, wsReconnectAttempts);
        wsReconnectAttempts++;
        wsReconnectTimer = setTimeout(() => {
          get().connectWebSocket();
        }, delay);
      }
    }
  },

  disconnectWebSocket: () => {
    wsManuallyClosed = true;
    if (wsReconnectTimer) {
      clearTimeout(wsReconnectTimer);
      wsReconnectTimer = null;
    }
    if (wsConnection) {
      wsConnection.onclose = null;
      wsConnection.close();
      wsConnection = null;
    }
    wsReconnectAttempts = 0;
  },
}));

if (import.meta.hot) {
  import.meta.hot.accept();
}
